import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { clsx } from "clsx";
import { X, CheckCircle, AlertCircle, Info } from "lucide-react";

type ToastType = "success" | "error" | "info";

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
  toast: (t: Omit<Toast, "id">) => void;
}

const ToastContext = createContext<ToastContextValue>({ toast: () => {} });

export function useToast() {
  return useContext(ToastContext);
}

const ICONS: Record<ToastType, typeof CheckCircle> = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
};

const COLORS: Record<ToastType, string> = {
  success: "bg-green-50 border-green-200 text-green-800",
  error:   "bg-red-50 border-red-200 text-red-800",
  info:    "bg-blue-50 border-blue-200 text-blue-800",
};

const ICON_COLORS: Record<ToastType, string> = {
  success: "text-green-500",
  error:   "text-red-500",
  info:    "text-blue-500",
};

const ACTION_COLORS: Record<ToastType, string> = {
  success: "bg-green-700 text-white hover:bg-green-800",
  error:   "bg-red-700 text-white hover:bg-red-800",
  info:    "bg-blue-700 text-white hover:bg-blue-800",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((t: Omit<Toast, "id">) => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { ...t, id }]);
    // Keep actionable toasts longer so the user can act
    const duration = t.action ? 8000 : 5000;
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, duration);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast: addToast }}>
      {children}

      <div className="pointer-events-none fixed bottom-0 right-0 z-50 flex flex-col items-end gap-2 p-4 sm:p-6">
        {toasts.map((t) => {
          const Icon = ICONS[t.type];
          return (
            <div
              key={t.id}
              className={clsx(
                "pointer-events-auto w-full max-w-sm animate-slide-up rounded-xl border p-4 shadow-lg",
                COLORS[t.type],
              )}
            >
              <div className="flex items-start gap-3">
                <Icon className={clsx("mt-0.5 h-5 w-5 shrink-0", ICON_COLORS[t.type])} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{t.title}</p>
                  {t.description && (
                    <p className="mt-0.5 text-sm opacity-80">{t.description}</p>
                  )}
                  {t.action && (
                    <button
                      onClick={() => { t.action!.onClick(); removeToast(t.id); }}
                      className={clsx(
                        "mt-2.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors",
                        ACTION_COLORS[t.type],
                      )}
                    >
                      {t.action.label}
                    </button>
                  )}
                </div>
                <button
                  onClick={() => removeToast(t.id)}
                  className="shrink-0 rounded p-0.5 opacity-60 hover:opacity-100 transition-opacity"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(1rem); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.2s ease-out; }
      `}</style>
    </ToastContext.Provider>
  );
}
